import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, UserRound } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useClients } from './hooks';
import type { ClientRow, ClientFilters } from './hooks';
import { ClientDetailPanel } from './ClientDetailPanel';
import { ClientFormPanel } from './ClientFormPanel';
import { MetricCard } from '../../components/common/MetricCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';
import { SortableHeader } from '../../components/common/SortableHeader';
import { PaginationFooter } from '../../components/common/PaginationFooter';
import { usePagination } from '../../hooks/usePagination';
import { useSortState } from '../../hooks/useSortState';

const TD: React.CSSProperties = { fontSize: 13, color: palette.g700, padding: '10px 12px', borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap' };

const TYPE_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  individual: { label: 'Individual', bg: palette.blueWash, fg: palette.blue },
  company: { label: 'Company', bg: '#EDE9FE', fg: '#7C3AED' },
};
const SOURCE_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  manual: { label: 'Manual', bg: palette.g100, fg: palette.g500 },
  booking_page: { label: 'Booking', bg: palette.accentWash, fg: palette.accentDeep },
  invoice: { label: 'Invoice', bg: palette.greenWash, fg: palette.green },
  code: { label: 'Code', bg: palette.blueWash, fg: palette.blue },
  // Item 11 Import Clients paths (migration 126). Conversational matches the
  // manual badge style — typing clients in by hand is spiritually "manual."
  // The other three paths each get a distinct muted palette entry so users
  // can tell at a glance where a client came from.
  import_conversational: { label: 'Typed in', bg: palette.g100, fg: palette.g500 },
  import_spreadsheet: { label: 'Spreadsheet', bg: palette.purpleWash, fg: palette.purple },
  import_phone_contacts: { label: 'Phone contacts', bg: palette.indigoWash, fg: palette.indigo },
  import_competitor: { label: 'Imported from another tool', bg: palette.amberWash, fg: palette.amber },
};

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0 })}`; }
// Q12 A-006: defensive fallback for source enums not yet in SOURCE_BADGE.
function humanizeEnum(s: string): string {
  return s.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
}

export function ClientsPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);

  const pagination = usePagination('clients');
  const sort = useSortState('created_at', 'desc');

  const { clients, totalCount, sources, loading, error, summary, filters, setFilters, createClient, updateClient } = useClients(business?.id, {
    from: pagination.from,
    to: pagination.to,
    sortColumn: sort.column,
    sortDirection: sort.direction,
  });

  const [selected, setSelected] = useState<ClientRow | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientRow | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => { document.title = 'CrewBit · Clients'; }, []);

  // Seed the source filter from a `?source=` query param so deep links from
  // the Setup Guide (Item 11 "See them in Clients" after an import) land on
  // a pre-filtered view. Applied once per mount.
  const [searchParams] = useSearchParams();
  const seededFromUrl = useRef(false);
  useEffect(() => {
    if (seededFromUrl.current) return;
    const paramSource = searchParams.get('source');
    if (paramSource) {
      setFilters(prev => ({ ...prev, source: paramSource }));
      seededFromUrl.current = true;
    }
  }, [searchParams, setFilters]);

  const handleFilterChange = useCallback((newFilters: ClientFilters) => {
    setFilters(newFilters);
    pagination.setPage(1);
  }, [setFilters, pagination]);

  if (loading && totalCount === null) return <div><Skeleton width={100} height={24} style={{ marginBottom: 24 }} /><div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}><TableSkeleton rows={5} columns={6} /></div></div>;
  if (error) return <div><div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Clients</div><div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div></div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Clients</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Client management and history</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8, border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600, color: palette.white, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={15} /> Add Client</button>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Total clients" value={String(summary.total)} />
        <MetricCard label="New this month" value={String(summary.newThisMonth)} />
        <MetricCard label="Individuals" value={String(summary.individuals)} />
        {summary.topClient && <MetricCard label="Top client" value={summary.topClient.name} sub={fmt$(summary.topClient.revenue)} />}
      </div>

      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20, boxShadow: shadow.card }}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={filters.search} onChange={e => handleFilterChange({ ...filters, search: e.target.value })} placeholder="Search name, email, phone..."
            style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 240 }} />
          <select value={filters.clientType ?? ''} onChange={e => handleFilterChange({ ...filters, clientType: e.target.value || null })}
            style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
            <option value="">All Types</option>
            <option value="individual">Individual</option>
            <option value="company">Company</option>
          </select>
          {sources.length > 0 && (
            <select value={filters.source ?? ''} onChange={e => handleFilterChange({ ...filters, source: e.target.value || null })}
              style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
              <option value="">All Sources</option>
              {sources.map(s => <option key={s} value={s}>{SOURCE_BADGE[s]?.label ?? humanizeEnum(s)}</option>)}
            </select>
          )}
        </div>

        {/* Table */}
        {clients.length === 0 && !loading ? (
          <EmptyState icon={<UserRound size={22} />} title="No clients yet" subtitle="Clients who connect with your business will appear here." />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <SortableHeader column="name" label="Name" sort={sort} />
              <th style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' }}>Company</th>
              <th style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' }}>Email</th>
              <th style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' }}>Phone</th>
              <SortableHeader column="client_type" label="Type" sort={sort} />
              <SortableHeader column="source" label="Source" sort={sort} />
              <th style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'right', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' }}>Bookings</th>
              <th style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'right', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' }}>Revenue</th>
            </tr></thead>
            <tbody>
              {clients.map(c => {
                const tp = TYPE_BADGE[c.client_type ?? 'individual'] ?? TYPE_BADGE.individual;
                const sr = SOURCE_BADGE[c.source] ?? SOURCE_BADGE.manual;
                return (
                  <tr key={c.id} onClick={() => setSelected(c)} style={{ cursor: 'pointer', transition: `background ${transition.fast}` }}
                    onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{c.name}</td>
                    <td style={TD}>{c.company_name ?? '—'}</td>
                    <td style={TD}>{c.email ?? '—'}</td>
                    <td style={TD}>{c.phone ?? '—'}</td>
                    <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: tp.bg, color: tp.fg }}>{tp.label}</span></td>
                    <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: sr.bg, color: sr.fg }}>{sr.label}</span></td>
                    <td style={{ ...TD, textAlign: 'right' }}>{c.booking_count || '—'}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{c.invoice_total ? fmt$(c.invoice_total) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <PaginationFooter pagination={pagination} totalCount={totalCount} loading={loading} />
      </div>

      {/* Detail panel */}
      <ClientDetailPanel client={selected} businessId={business?.id ?? ''} userId={user?.id ?? ''}
        onClose={() => setSelected(null)} onEdit={(c) => { setSelected(null); setEditingClient(c); }}
        onDisconnected={() => setToast({ msg: 'Client disconnected', type: 'success' })} />

      {/* Create/Edit panel */}
      <ClientFormPanel isOpen={showCreate || !!editingClient} client={editingClient}
        onClose={() => { setShowCreate(false); setEditingClient(null); }}
        onSave={async (data) => {
          if (!user) return;
          if (editingClient) {
            const ok = await updateClient(editingClient.id, data, user.id);
            if (ok) { setToast({ msg: 'Client updated', type: 'success' }); setEditingClient(null); }
          } else {
            const id = await createClient(data, user.id);
            if (id) { setToast({ msg: 'Client created', type: 'success' }); setShowCreate(false); }
          }
        }}
      />

      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
